import {
  IDepartmentData,
  IGroupInfo,
  IInfoType,
  IMessageData,
  ISelectedMembers,
  ISuggestedClinicianData,
} from "@home-health-notify/domain";
import { Box, Divider, Typography } from "@mui/material";
import {
  ApiErrorHandler,
  convertMetersToMilesEnum,
  getDisciplineByDepartmentId,
  IOrganizationContext,
  OrganizationContext,
  useSmartContent,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import AdvanceSearch from "../../../../addMember/addUsers/advanceSearch/AdvanceSearch";
import {
  getAdvanceSearchFilters,
  getPatientHeaderProps,
} from "../../../../addMember/addUsers/advanceSearch/map/AdvanceMapHelper";
import { IMessageContentTypeBubble } from "../../../MessagePanel";
import {
  AdvancedSearchWrapper,
  SmartContentMessage,
  SmartContentWrapper,
} from "./SmartContent.style";
import { SmartContentItem } from "./SmartContentItem";
import { useContext, useMemo } from "react";
import ExpandableText from "../../../../common/ExpandableText";

export const SmartContent = ({
  message,
  chatInfoAccess,
}: IMessageContentTypeBubble) => {
  const { selectedOrgUser }: IOrganizationContext =
    useContext(OrganizationContext);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const onAddMembers = async (selectedMembers: ISelectedMembers) => {
    const chatId: string = groupInfo.id;
    try {
      if (selectedOrgUser) {
        await selectedOrgUser.chatManager
          .getPatientChatManager()
          .addMembers(selectedMembers, chatId);

        enqueueSnackbar(groupInfo.name + tNs("membersAddedSuccessfully"), {
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      ApiErrorHandler(error, enqueueSnackbar);
    }
  };

  const groupInfo: IInfoType = chatInfoAccess?.chatInfo as IGroupInfo;
  let messageData: IMessageData = message.getMessage();
  const departments: IDepartmentData[] =
    selectedOrgUser?.getDepartments() ?? [];
  const suggestedCliniciansDepartments =
    (messageData.suggestedCliniciansDepartments &&
      messageData.suggestedCliniciansDepartments[0]) ??
    null;
  const suggestedDiscipline = getDisciplineByDepartmentId(
    suggestedCliniciansDepartments ?? "",
    departments,
  );

  const tNs = (key: string) =>
    t(key, { ns: "message", clinicians: suggestedDiscipline });

  const defaultInfoType = {} as IInfoType;
  const { getSmartContentMessage, isMemberInGroup, isReferralMatch } =
    useSmartContent(chatInfoAccess?.chatInfo ?? defaultInfoType);

  const advancedSearchFilters = useMemo(
    () => getAdvanceSearchFilters(groupInfo),
    [groupInfo],
  );
  const patientHeaderProps = useMemo(
    () => getPatientHeaderProps(groupInfo),
    [groupInfo],
  );
  const memoizedSuggestedCliniciansDepartments = useMemo(
    () => messageData.suggestedCliniciansDepartments,
    [messageData.suggestedCliniciansDepartments],
  );
  const searchRadius = useMemo(
    () =>
      convertMetersToMilesEnum(
        messageData.suggestedCliniciansSearchRadius ?? 0,
      ),
    [messageData.suggestedCliniciansSearchRadius],
  );

  return (
    <>
      {groupInfo && (
        <Box data-testid="smart-content-layout">
          <SmartContentWrapper>
            <SmartContentMessage>
              <ExpandableText>
                {getSmartContentMessage(messageData)}
              </ExpandableText>
            </SmartContentMessage>
            {(messageData.suggestedClinicians ?? []).map(
              (clinician: ISuggestedClinicianData, index: number) => (
                <SmartContentItem
                  clinicianData={clinician}
                  groupInfo={groupInfo}
                  key={index}
                  isMemberInGroup={isMemberInGroup(clinician)}
                  isReferralMatch={isReferralMatch(clinician)}
                  message={message}
                />
              ),
            )}
          </SmartContentWrapper>
          <Divider />
          <AdvancedSearchWrapper>
            <Typography>{tNs("smartContent.advancedSearchTitle")}</Typography>
            <AdvanceSearch
              advanceSearchFilters={advancedSearchFilters}
              existingMembers={groupInfo.members}
              fullWidth={false}
              resetAddedMembersOnClose={true}
              patientHeaderProps={patientHeaderProps}
              suggestedCliniciansDepartments={
                memoizedSuggestedCliniciansDepartments
              }
              searchRadius={searchRadius}
              onAddMemberCallback={onAddMembers}
              openedFrom="smart_content"
            />
          </AdvancedSearchWrapper>
        </Box>
      )}
    </>
  );
};
